/**
 *
 * This service manages payment methods of the user
 *
 */
import { Injectable, signal } from '@angular/core';
import {
    Card,
    CardMethodsType,
    CardTypes,
    ComponentPaymentMethodName,
    CreatePayMethodRequest,
    CreatePayMethodResponse,
    CreditMethodsType,
    CustomerProfile,
    Echeck,
    Exception,
    ForteException,
    OrderIdPaymentMethod,
    PaymentMethod,
    PaymentMethodItem,
    PaymentMethods,
    PaymentMethodsEvents,
    PaymentMethodsLatest,
    PaymentMethodsType,
    PaymentToken,
    ProcessorName,
} from '@cargos/sprintpay-models';
import {
    AdyenService,
    ErrorForteHandlerService,
    IPService,
    LoggerService,
    PaymentMethodsRequestService,
} from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap, take, throwError } from 'rxjs';
import { CurrentFacilityService } from './current-facility.service';
import { CustomerService } from './customer.service';
import { ErrorCustomHandlerService } from './http-error-handler.service';
import { SessionService } from './session.service';
import { PaymentMethodDetails, PaymentMethodSelected } from './summary/models/types';

export interface FilteredPaymentMethodsResponse {
    methodsForThreshold: PaymentMethod[];
    methods: PaymentMethod[];
}

export const CARD_TYPES_MAP = Object.freeze({
    amex: CardTypes.AMEX,
    discover: CardTypes.DISCOVER,
    jcb: CardTypes.JCB,
    mc: CardTypes.MASTERCARD,
    visa: CardTypes.VISA,
});

@Injectable({
    providedIn: 'root',
})
export class PaymentMethodsService {
    public validateEcheck = signal<PaymentMethodItem | null>(null);
    public validateEcheck$: BehaviorSubject<PaymentMethodItem | null> = new BehaviorSubject<PaymentMethodItem | null>(
        null
    );

    readonly CACHE_DURATION_IN_MINUTES = 3;
    readonly INITIAL_DATE = dayjs(new Date()).toDate();

    public guestPaymentMethods: PaymentMethodsType[] = [
        PaymentMethods.CREDIT_CARD,
        PaymentMethods.PAYPAL,
        PaymentMethods.ECHECK,
    ];

    private _paymentMethods = new BehaviorSubject<PaymentMethod[]>([]);
    private _expireTime = new BehaviorSubject<Date>(this.INITIAL_DATE);
    private handlerEvent: BehaviorSubject<PaymentMethodsEvents | null> =
        new BehaviorSubject<PaymentMethodsEvents | null>(null);
    private getPaymentMethodsInProcess = new BehaviorSubject<boolean>(false);
    private reverifyEcheck: Echeck | null;
    private eventAboveThreshold = new BehaviorSubject<boolean>(false);
    private paymentMethodsProhibitedForBelowThreshold: PaymentMethods[] = [PaymentMethods.PAYPAL];
    private buyerIPV4Address: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private paymentMethodsGuest = new BehaviorSubject<PaymentMethod[]>([]);

    constructor(
        private paymentMethodsRequestService: PaymentMethodsRequestService,
        private sessionService: SessionService,
        private errorForteHandlerService: ErrorForteHandlerService,
        private currentFacilityService: CurrentFacilityService,
        private customerService: CustomerService,
        private loggerService: LoggerService,
        private ipService: IPService,
        private adyenService: AdyenService,
        private errorCustomHandlerService: ErrorCustomHandlerService
    ) {
        this.getIp();
    }

    get instant_payment_methods(): PaymentMethod[] {
        return this._paymentMethods.value;
    }

    setPaymentMethods(paymentMethods: PaymentMethod[]): void {
        this._expireTime.next(dayjs(new Date()).add(this.CACHE_DURATION_IN_MINUTES, 'minutes').toDate());
        this._paymentMethods.next(paymentMethods);
    }

    getPaymentMethods$(): Observable<PaymentMethod[]> {
        return this._paymentMethods.asObservable();
    }

    /**
     * This method is used to check if the event was generated above the threshold
     */
    get instant_event_above_threshold(): boolean {
        return this.eventAboveThreshold.value;
    }

    private setIsPaymentMethodsLoading(isLoading: boolean): void {
        this.getPaymentMethodsInProcess.next(isLoading);
    }

    private buildPaymentsMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
        try {
            paymentMethods.forEach((paymentMethod) => {
                if (PaymentMethod.isCreditMethod(paymentMethod.name as CreditMethodsType)) {
                    const [avaliableCredit, creditPercentage] = this.calculateSprintpayCredit(
                        paymentMethod.balance?.avaliableCredit,
                        paymentMethod.balance?.creditLimit
                    );

                    paymentMethod.balance = {
                        avaliableCredit,
                        creditPercentage,
                        creditLimit: paymentMethod.balance?.creditLimit,
                    };
                }
            });
            return paymentMethods;
        } catch (error) {
            return paymentMethods;
        }
    }

    getPaymentMethodsRequest$(): Observable<PaymentMethod[]> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                if (isAuthenticated) {
                    this.setIsPaymentMethodsLoading(true);
                    return this.paymentMethodsRequestService.getPaymentMethods().pipe(
                        map((paymentMethods: PaymentMethod[]) => this.updateCardType(paymentMethods)),
                        map((paymentMethods: PaymentMethod[]) => this.buildPaymentsMethods(paymentMethods)),
                        map((paymentMethods: PaymentMethod[]) => this.sortPaymentMethods(paymentMethods)),
                        map((paymentMethods: PaymentMethod[]) => {
                            this.setPaymentMethods(paymentMethods);
                            this.setIsPaymentMethodsLoading(false);
                            return paymentMethods;
                        }),
                        catchError((error: unknown) => {
                            this.loggerService.captureException(
                                this.errorCustomHandlerService.normalizeToError(error),
                                {
                                    level: 'fatal',
                                    tags: {
                                        api: 'payment-methods',
                                    },
                                    extra: {
                                        error,
                                    },
                                    user: {
                                        email: this.customerService.getCustomerEmail() || '',
                                    },
                                }
                            );
                            this.setPaymentMethods([]);
                            this.setIsPaymentMethodsLoading(false);
                            return throwError(() => error);
                        })
                    );
                }
                this.setIsPaymentMethodsLoading(true);
                return forkJoin([
                    this.getGuestPaymentMethod(),
                    this.paymentMethodsRequestService.getPaymentMethods(),
                ]).pipe(
                    map(([guestPaymentMethods, fromAPIPaymentMethods]: [PaymentMethod[], PaymentMethod[]]) => {
                        guestPaymentMethods.forEach((guestMethod) => {
                            const matchingApiMethod = fromAPIPaymentMethods.find(
                                (apiMethod) => apiMethod.name === guestMethod.name
                            );
                            if (matchingApiMethod) {
                                guestMethod.processorName = matchingApiMethod.processorName;
                            }
                        });
                        return guestPaymentMethods;
                    }),
                    map((paymentMethods: PaymentMethod[]) => this.updateCardType(paymentMethods)),
                    map((paymentMethods: PaymentMethod[]) => this.buildPaymentsMethods(paymentMethods)),
                    map((paymentMethods: PaymentMethod[]) => this.sortPaymentMethods(paymentMethods)),
                    map((paymentMethods: PaymentMethod[]) => {
                        this.setPaymentMethods(paymentMethods);
                        this.setIsPaymentMethodsLoading(false);
                        return paymentMethods;
                    })
                );
            })
        );
    }

    getGuestPaymentMethod(): Observable<PaymentMethod[]> {
        const paymentMethodArray: PaymentMethod[] = [];

        this.guestPaymentMethods.map((method, index) => {
            const token = this.getGuestPaymentToken(method);

            const paymentMethod = PaymentMethod.fromJson({
                id: 0,
                name: method,
                paymentToken: token
                    ? token
                    : {
                          token: null,
                          paymentMethod: null,
                          paymentProcessor: null,
                      },
                balance: {},
                items: [],
                orderId: index,
            });

            paymentMethodArray.push(paymentMethod);
        });
        return of(paymentMethodArray);
    }

    getGuestPaymentToken(method: PaymentMethods): PaymentToken | undefined {
        const sourceToken = this.currentFacilityService.getSource();

        if (!sourceToken?.enabledPaymentMethods) {
            return undefined;
        }

        return sourceToken.enabledPaymentMethods.find(
            (guestMethod) => guestMethod.paymentMethod?.componentName === method
        )?.paymentMethod?.paymentToken;
    }

    /**
     * Retrieves the payment method of the customer based on the specified type.
     * @param type The type of payment method to search for. Valid values are 'CREDIT_CARD', 'ECHECK', 'CARGO_CREDIT', 'SIGNET', or 'PAYPAL'.
     * @returns An Observable that emits the matching PaymentMethod object, or undefined if not found.
     * @example getPaymentMethodsByType(PaymentMethods.CREDIT_CARD)
     */
    getPaymentMethodsByType(type: PaymentMethodsType): Observable<PaymentMethod | undefined> {
        return this._paymentMethods.pipe(
            map((paymentMethods: PaymentMethod[]) => {
                return paymentMethods.find((paymentMethod) => paymentMethod.name === type);
            })
        );
    }

    sortPaymentMethods(paymentMethods: PaymentMethod[], ordered = true): PaymentMethod[] {
        if (!ordered) {
            return paymentMethods;
        }

        paymentMethods.forEach((paymentMethod) => {
            paymentMethod.orderId = OrderIdPaymentMethod[paymentMethod.getComponentName()];
        });

        return paymentMethods.sort((a, b) => {
            if (a.orderId === undefined) return 1;
            if (b.orderId === undefined) return -1;
            return a.orderId - b.orderId;
        });
    }

    private buildPaymentToken(paymentMethodSelected: PaymentMethod, token?: string): PaymentToken | undefined {
        if (PaymentMethods.CARD_METHODS.includes(paymentMethodSelected.name as CardMethodsType) && token) {
            const item = paymentMethodSelected.items.find((item) => {
                return item.paymentToken?.token === token;
            });

            if (item?.paymentToken) {
                item.paymentToken.paymentMethod = {
                    name: paymentMethodSelected.getComponentName(),
                    logTime: PaymentMethods.CREDIT_CARD === paymentMethodSelected.name ? 10 : 2,
                    componentName: paymentMethodSelected.name,
                };
                if (PaymentMethods.CREDIT_CARD === paymentMethodSelected.name) {
                    ((item.paymentToken.paymentMethodNumber = item?.accountingDetails?.lastFourDigits),
                        (item.paymentToken.paymentInstitution = item?.accountingDetails?.cardType),
                        (item.paymentToken.paymentMethodExpirationMonth = item?.accountingDetails?.expirationMonth));
                    item.paymentToken.paymentMethodExpirationYear = item?.accountingDetails?.expirationYear;
                    item.paymentToken.buyerIPV4Address = this.buyerIPV4Address.value || undefined;
                }
            }

            return item?.paymentToken;
        }

        return paymentMethodSelected.paymentToken;
    }

    /**
     *
     * @returns {PaymentToken} The paymentToken
     * @param {PaymentMethodsType} type Type of payment to search 'CREDIT_CARD' | 'ECHECK' | 'CARGO_CREDIT' | 'SIGNET' | 'PAYPAL',
     * @param {string} token Token by item of credit card or echeck
     */
    getPaymentToken(type: PaymentMethodsType, token?: string): Observable<PaymentToken | null> {
        return this.getPaymentMethodsByType(type).pipe(
            map((paymentMethod: PaymentMethod | undefined) => {
                if (paymentMethod) {
                    return this.buildPaymentToken(paymentMethod, token) || null;
                }

                return null;
            })
        );
    }

    getMethod(paymentMethodSelected: PaymentMethod, token?: string): PaymentMethod | PaymentMethodItem | undefined {
        if (PaymentMethods.CARD_METHODS.includes(paymentMethodSelected.name as CardMethodsType) && token) {
            return paymentMethodSelected.items.find((item) => {
                return item.paymentToken?.token === token;
            });
        }

        return paymentMethodSelected;
    }

    getPaymentMethodByTypeAndToken(
        type: PaymentMethodsType,
        token?: string
    ): Observable<PaymentMethod | PaymentMethodItem | null> {
        return this.getPaymentMethodsByType(type).pipe(
            map((paymentMethod: PaymentMethod | undefined) => {
                if (paymentMethod) {
                    return this.getMethod(paymentMethod, token) || null;
                }

                return null;
            })
        );
    }

    getCargoCreditBalance(): Observable<{
        isValid: boolean;
        exception?: Exception;
        availableCredit?: boolean;
        invalidPaymentMethod?: boolean;
    }> {
        return this.isCargoCreditAvailable().pipe(
            switchMap((isCargoCreditAvailable: boolean) => {
                if (isCargoCreditAvailable) {
                    const componentName = PaymentMethod.getComponentNameByType(PaymentMethods.SPRINTPAY_FLEX);
                    return this.paymentMethodsRequestService.getCreditBalance(componentName).pipe(
                        map((newBalance) => {
                            let newArr;
                            if (newBalance === 0) {
                                return {
                                    isValid: false,
                                    availableCredit: false,
                                    exception: Exception.fromJson({
                                        description:
                                            "It seems you don't have enough credit for your next transaction, please contact collections@cargosprint.com to get help",
                                    }),
                                };
                            }
                            if (!!this.instant_payment_methods.length) {
                                newArr = this.instant_payment_methods.map((obj: PaymentMethod) => {
                                    return PaymentMethod.fromJson({
                                        ...obj,
                                        balance: {
                                            ...obj.balance,
                                            avaliableCredit: newBalance,
                                        },
                                    });
                                });
                            } else {
                                newArr = [
                                    {
                                        id: 94,
                                        name: PaymentMethods.SPRINTPAY_FLEX,
                                        balance: {
                                            avaliableCredit: newBalance,
                                        },
                                        items: [],
                                        orderId: 7,
                                    },
                                ];
                            }
                            this.setPaymentMethods(newArr);

                            return {
                                isValid: true,
                                availableCredit: newBalance > 0,
                                invalidPaymentMethod: false,
                            };
                        }),
                        catchError((error) => {
                            return of({
                                isValid: false,
                                invalidPaymentMethod: true,
                                exception: Exception.fromJson({
                                    description: 'Failed to load available credit details. Please try again later.',
                                }),
                            });
                        })
                    );
                }
                return of({
                    isValid: false,
                    invalidPaymentMethod: true,
                    exception: Exception.fromJson({
                        description: 'This user does not have CargoSprint credit enabled',
                    }),
                });
            })
        );
    }

    removeEcheck(echeck: PaymentMethodItem, emitChange = true): Observable<string> {
        return of(echeck.paymentToken?.paymentProcessor?.name).pipe(
            switchMap(() => {
                if (echeck.paymentToken?.paymentProcessor?.name === ProcessorName.Forte) {
                    return this.paymentMethodsRequestService.removeEcheck(echeck.paymentToken?.token || '');
                }

                return this.paymentMethodsRequestService.removeBankAccount(Number(echeck.paymentToken?.token));
            }),
            map((response) => {
                if (emitChange) {
                    this.newEventPaymentMethod(PaymentMethodsEvents.echeckRemoved);
                }
                return response;
            }),
            catchError((error: ForteException) => throwError(() => this.errorForteHandlerService.handlerError(error)))
        );
    }

    removeCard(token: string, emitChange = true): Observable<string> {
        return this.adyenService.removeAdyenCard(token).pipe(
            map((response) => {
                if (emitChange) {
                    this.newEventPaymentMethod(PaymentMethodsEvents.creditCardRemoved);
                }

                return response;
            }),
            catchError((error: Exception) => {
                return throwError(() => {
                    return error;
                });
            })
        );
    }

    getCards(): Observable<Card[]> {
        return this.paymentMethodsRequestService.getCards();
    }

    newEvent(event: PaymentMethodsEvents): void {
        this.handlerEvent.next(event);
    }
    newEventPaymentMethod(event: PaymentMethodsEvents, eventAboveThreshold?: boolean): void {
        this.eventAboveThreshold.next(!!eventAboveThreshold);
        this.handlerEvent.next(event);
    }

    onEventPaymentMethod$(): Observable<PaymentMethodsEvents | null> {
        return this.handlerEvent.asObservable();
    }

    createPayEcheckMethod(paymentMethodInfo: CreatePayMethodRequest): Observable<string> {
        return this.paymentMethodsRequestService
            .createPayMethod(paymentMethodInfo)
            .pipe(
                map(
                    (createPayMethodResponse: CreatePayMethodResponse) => createPayMethodResponse?.paymethod_token || ''
                )
            );
    }

    /**
     * Filters payment methods based on the threshold condition.
     * @param methods The list of payment methods to filter.
     * @param condition The threshold condition (true, false, or undefined).
     * @returns The filtered list of payment methods.
     */
    private filterPaymentMethods(methods: PaymentMethod[], condition?: boolean): PaymentMethod[] {
        if (condition) {
            return methods.filter((method) => method.thresholdPaymentMethod);
        }
        if (condition === false) {
            return methods.filter((method) => method.defaultPaymentMethod || method.defaultPaymentMethod === undefined);
        }
        if (!methods || !Array.isArray(methods)) {
            return [];
        }
        if (methods.length === 0) {
            return [];
        }

        return methods;
    }

    /**
     * Retrieves filtered payment methods, separating them into those above the threshold and default methods.
     * @returns An Observable that emits an object containing the filtered payment methods.
     */
    getFilteredPaymentMethods$(): Observable<FilteredPaymentMethodsResponse> {
        return this.getPaymentMethods$().pipe(
            map((paymentMethods) => {
                return {
                    methodsForThreshold: this.filterPaymentMethods(paymentMethods, true),
                    methods: this.filterPaymentMethods(paymentMethods, false),
                };
            })
        );
    }

    isPaymentMethodAvailable(method: PaymentMethodsType, aboveThreshold?: boolean): boolean {
        const filteredMethods = this.filterPaymentMethods(this.instant_payment_methods, aboveThreshold);
        return filteredMethods.some((paymentMethod) => paymentMethod.name === method);
    }

    getCreditAvailablePaymentMethod(): PaymentMethodsType | null {
        const preferredMethods: PaymentMethodsType[] = [
            PaymentMethods.CARGO_CREDIT,
            PaymentMethods.SPRINTPAY_FLEX,
            PaymentMethods.FINANCE,
        ];

        for (const method of preferredMethods) {
            if (this.isPaymentMethodAvailable(method)) {
                return method;
            }
        }
        return null;
    }

    getPaymentMethodsByType$(
        type: PaymentMethodsType,
        aboveThreshold?: boolean
    ): Observable<PaymentMethod | undefined> {
        return this.getPaymentMethods$().pipe(
            map((paymentMethods) => {
                const filteredMethods = this.filterPaymentMethods(paymentMethods, aboveThreshold);
                return filteredMethods.find((method) => method.name === type);
            })
        );
    }

    getCreditAvailableByPaymentMethod$(
        paymentMethod: PaymentMethodsType = PaymentMethods.CARGO_CREDIT
    ): Observable<number> {
        return this.getPaymentMethodsByType$(paymentMethod).pipe(
            map((paymentMethod) => paymentMethod?.balance.avaliableCredit || 0)
        );
    }

    calculateSprintpayCredit(creditAvailable: number = 0, creditLimit: number = 0): [number, number] {
        const availableCredit =
            creditAvailable <= 0 || creditLimit <= 0
                ? 0
                : creditAvailable > creditLimit
                  ? creditLimit
                  : creditAvailable;

        const creditPercentage =
            availableCredit === 0 || creditLimit <= 0
                ? 0
                : Math.round(((availableCredit * 100) / creditLimit) * 10) / 10;

        return [availableCredit, creditPercentage];
    }

    isCargoCreditAvailable(): Observable<boolean> {
        return this.customerService.getCustomerProfile().pipe(
            map((customerProfile: CustomerProfile | undefined) => {
                return !!customerProfile?.components?.find((components) => {
                    return PaymentMethod.isCreditMethod(components?.name as CreditMethodsType);
                });
            })
        );
    }

    getReverifyEcheck(): Echeck | null {
        return this.reverifyEcheck;
    }

    setReverifyEcheck(echeck: Echeck | null): void {
        this.reverifyEcheck = echeck;
    }

    /**
     * @method getPaymentMethodValidated()
     * @description Validates and returns the available payment method for the customer.
     * @param {PaymentMethodsType} method - The payment method type to validate.
     * @param {PaymentMethod[]} paymentMethods - The list of available payment methods.
     * @param {PaymentMethodDetails} [paymentDetails] - The details of the latest payment method used.
     * @returns {Observable<PaymentMethodSelected | null>} An observable that emits the selected payment method or null if not available.
     */
    getPaymentMethodValidated(
        method: PaymentMethodsType,
        paymentMethods: PaymentMethod[],
        paymentDetails?: PaymentMethodDetails
    ): Observable<PaymentMethodSelected | null> {
        const isPaymentMethodAvailable = this.isPaymentMethodAvailable(method);
        if (!isPaymentMethodAvailable) {
            return of(null);
        }

        if (method === PaymentMethods.CREDIT_CARD) {
            const creditCards = paymentMethods.find((method) => method.name === PaymentMethods.CREDIT_CARD);

            if (paymentDetails && creditCards && !!creditCards?.items.length) {
                const lastCreditCard = creditCards.items.find(
                    (card) =>
                        card.accountingDetails?.lastFourDigits === paymentDetails.last4 &&
                        card.accountingDetails?.expirationYear === paymentDetails.expirationYear &&
                        card.accountingDetails?.expirationMonth === paymentDetails.expirationMonth
                );

                if (lastCreditCard) {
                    return of({
                        paymentAccount: {
                            ...lastCreditCard,
                            paymentToken: {
                                ...lastCreditCard?.paymentToken,
                                paymentMethod: {
                                    name: ComponentPaymentMethodName.CREDIT_CARD,
                                    logTime: 2,
                                    componentName: PaymentMethods.CREDIT_CARD,
                                },
                            },
                        },
                        method: PaymentMethods.CREDIT_CARD,
                        token: lastCreditCard.paymentToken?.token || undefined,
                    });
                }
            }

            if (creditCards && !!creditCards?.items.length) {
                return of({
                    paymentAccount: {
                        ...creditCards.items[0],
                        paymentToken: {
                            ...creditCards.items[0].paymentToken,
                            paymentMethod: {
                                name: ComponentPaymentMethodName.CREDIT_CARD,
                                logTime: 2,
                                componentName: PaymentMethods.CREDIT_CARD,
                            },
                        },
                    },
                    method: PaymentMethods.CREDIT_CARD,
                    token: creditCards.items[0].paymentToken?.token || undefined,
                });
            }

            return of({
                paymentAccount: null,
                method: PaymentMethods.CREDIT_CARD,
            });
        }

        // * Note: Echecks were not considered to be returned by the API when they are the latest payment method used.
        if (method === PaymentMethods.ECHECK) {
            const echecks = paymentMethods.find((method) => method.name === PaymentMethods.ECHECK);

            if (echecks && !!echecks?.items.length) {
                return of({
                    paymentAccount: {
                        ...echecks.items[0],
                        paymentToken: {
                            ...echecks.items[0].paymentToken,
                            paymentMethod: {
                                name: ComponentPaymentMethodName.ECHECK,
                                logTime: 2,
                                componentName: PaymentMethods.ECHECK,
                            },
                        },
                    },
                    method: PaymentMethods.ECHECK,
                    token: echecks.items[0].paymentToken?.token || undefined,
                });
            }

            return of({
                paymentAccount: null,
                method: PaymentMethods.ECHECK,
            });
        }

        if (method === PaymentMethods.PAYPAL) {
            return of({ paymentAccount: true, method: PaymentMethods.PAYPAL });
        }

        return of(null);
    }

    getFrequentPaymentMethodAvailable(
        paymentMethodsLatest: PaymentMethodsLatest[],
        paymentMethods: PaymentMethod[]
    ): PaymentMethodsLatest | undefined {
        return paymentMethodsLatest.find((paymentMethodLatest: PaymentMethodsLatest) => {
            const paymentMethodName: PaymentMethodsType = paymentMethodLatest.paymentMethod.componentName;
            const hasPaymentMethod = paymentMethods.find((method) => method.name === paymentMethodName);

            return hasPaymentMethod;
        });
    }

    isPaymentMethodsSelectedOnCartAboveValid(paymentMethod: PaymentMethodSelected): boolean {
        return !this.paymentMethodsProhibitedForBelowThreshold.includes(paymentMethod.method);
    }

    setValidateEcheckState(state: PaymentMethodItem | null): void {
        this.validateEcheck$.next(state);
    }

    getValidateEcheckState(): Observable<PaymentMethodItem | null> {
        return this.validateEcheck$.asObservable();
    }

    getGuestPaymentMethods$(): Observable<PaymentMethod[]> {
        return this.paymentMethodsGuest.asObservable();
    }

    getGuestPaymentMethodsByType$(paymentMehod: PaymentMethodsType): Observable<PaymentMethod[]> {
        return this.paymentMethodsGuest
            .asObservable()
            .pipe(map((methods) => methods.filter((method) => method.name === paymentMehod)));
    }

    setGuestPaymentMethods(methods: PaymentMethod[]): void {
        this.paymentMethodsGuest.next(methods);
    }

    updateCardType(paymentMethods: PaymentMethod[]): PaymentMethod[] {
        paymentMethods.forEach((paymentMethod) => {
            if (paymentMethod.name === PaymentMethods.CREDIT_CARD) {
                paymentMethod.items.forEach((item) => {
                    const cardType = item.accountingDetails?.cardType;
                    if (cardType) {
                        item.accountingDetails!.cardType = CARD_TYPES_MAP[cardType] || cardType;
                    }
                });
            }
        });
        return paymentMethods;
    }

    private getIp(): void {
        this.ipService
            .getIP()
            .pipe(take(1))
            .subscribe({
                next: (ipAddress: string) => {
                    this.buyerIPV4Address.next(ipAddress);
                },
                error: () => {
                    this.buyerIPV4Address.next(null);
                },
            });
    }
}
